import { useEffect } from 'react';
import { useSiteFonts } from '@/context/SiteConfigContext';

const GOOGLE_FONTS_API = 'https://fonts.googleapis.com/css2';

function toGoogleFontParam(name: string): string {
  // Google Fonts API expects spaces as + and weights like :wght@400;700
  const normalized = name.trim().replace(/\s+/g, '+');
  return `family=${normalized}:wght@300;400;500;600;700`;
}

export function DynamicFonts() {
  const { primaryHeading, secondaryBody, accent, customCssUrl } = useSiteFonts();

  useEffect(() => {
    const families = [primaryHeading, secondaryBody, accent].filter(Boolean);
    const uniqueFamilies = Array.from(new Set(families));

    if (uniqueFamilies.length === 0) return;

    const url = `${GOOGLE_FONTS_API}?${uniqueFamilies.map(toGoogleFontParam).join('&')}&display=swap`;

    const id = 'dynamic-google-fonts';
    let link = document.getElementById(id) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    if (link.href !== url) {
      link.href = url;
    }

    // Apply CSS variables for dynamic font usage
    const varId = 'dynamic-font-variables';
    let style = document.getElementById(varId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = varId;
      document.head.appendChild(style);
    }

    style.textContent = `
      :root {
        --dynamic-heading: '${primaryHeading || 'Oswald'}';
        --dynamic-body: '${secondaryBody || 'Jost'}';
        --dynamic-elegant: '${accent || 'EB Garamond'}';
      }
    `;

    // Optional custom CSS URL
    if (customCssUrl) {
      const customId = 'dynamic-custom-font-css';
      let customLink = document.getElementById(customId) as HTMLLinkElement | null;
      if (!customLink) {
        customLink = document.createElement('link');
        customLink.id = customId;
        customLink.rel = 'stylesheet';
        document.head.appendChild(customLink);
      }
      if (customLink.href !== customCssUrl) {
        customLink.href = customCssUrl;
      }
    }

    return () => {
      // We intentionally do not remove the link on unmount so fonts remain available.
    };
  }, [primaryHeading, secondaryBody, accent, customCssUrl]);

  return null;
}
